import { HOLIDAYS as NATIONAL_HOLIDAYS } from "./holidays.js";
import { approvedAttendance } from "./work-logs.js";
import { addDays, wd } from "./dates.js";

const RULES = {
  sss: "SSS Circular 2024-006: 15% (EE 5% / ER 10%), MSC ₱5,000–₱35,000, EC ₱10 below MSC ₱15,000 else ₱30",
  phic: "RA 11223 §10; PhilHealth Circular 2020-0005: 5% of basic, floor ₱10,000, ceiling ₱100,000, shared equally",
  hdmf: "HDMF Circular 460: EE 2% (1% if ≤ ₱1,500), ER 2%, base capped at ₱10,000",
  wtc: "RA 10963 (TRAIN) §24(A)(2)(a) 2023 schedule; RR 11-2018 Annex E (official semi-monthly table)",
};

function contrib(m, basic = m) {
  // m = monthly basic in centavos
  const msc = Math.min(
    Math.max(Math.round(m / 50000) * 50000, 500000),
    3500000,
  );
  const pb = Math.min(Math.max(basic, 1000000), 10000000),
    prem = Math.round(pb * 0.05),
    per = Math.floor(prem / 2);
  const hb = Math.min(m, 1000000);
  return {
    sssEE: Math.round(msc * 0.05),
    sssER: Math.round(msc * 0.1),
    ec: msc < 1500000 ? 1000 : 3000,
    phEE: prem - per,
    phER: per,
    hdEE: Math.round(hb * (m <= 150000 ? 0.01 : 0.02)),
    hdER: Math.round(hb * 0.02),
  };
}

const BR = [
  [0, 0, 0],
  [25000000, 0, 0.15],
  [40000000, 2250000, 0.2],
  [80000000, 10250000, 0.25],
  [200000000, 40250000, 0.3],
  [800000000, 220250000, 0.35],
];

function annualTax(t) {
  let b = BR[0];
  for (const x of BR) if (t > x[0]) b = x;
  return Math.round(b[1] + (t - b[0]) * b[2]);
}

function payLine(e) {
  const c = contrib(e.basic * 100),
    h = (x) => Math.round(x / 2),
    gross = h(e.basic * 100);
  const L = {
    emp: e.id,
    gross,
    sssEE: h(c.sssEE),
    phEE: h(c.phEE),
    hdEE: h(c.hdEE),
    sssER: h(c.sssER),
    ec: h(c.ec),
    phER: h(c.phER),
    hdER: h(c.hdER),
  };
  const taxable = gross - L.sssEE - L.phEE - L.hdEE;
  L.tax = e.mwe ? 0 : semiMonthlyTax(Math.max(0, taxable));
  L.net = taxable - L.tax;
  L.er = L.sssER + L.ec + L.phER + L.hdER;
  return L;
}

const MINW = {
  VI: [
    [
      "2025-11-19",
      55000,
      "Region VI wage order effective 19 Nov 2025: ₱550 non-agriculture, more than 10 workers",
    ],
  ],
  "VII-A": [
    ["2025-10-04", 54000, "Wage Order ROVII-26, Class A"],
    ["2026-10-14", 58200, "Wage Order ROVII-27, Class A, from 14 Oct 2026"],
  ],
};

const REGION_NAME = {
  VI: "Western Visayas (Region VI)",
  "VII-A": "Central Visayas, Class A (Region VII)",
};

function minWage(rg, date) {
  const l = (MINW[rg] || []).filter((r) => r[0] <= date);
  return l.length
    ? {
        daily: l[l.length - 1][1],
        src: l[l.length - 1][2],
        from: l[l.length - 1][0],
      }
    : { daily: 0, src: "No wage order encoded for this region", from: "" };
}

const REG_HOL = NATIONAL_HOLIDAYS.filter((h) => h.kind === "REGULAR").map(
    (h) => h.date,
  ),
  SPC_HOL = NATIONAL_HOLIDAYS.filter((h) => h.kind === "SPECIAL").map(
    (h) => h.date,
  );

const MULT = { WORK: 0, REST: 0.3, REG: 1, SPC: 0.3 };

const DIRECT_DEPTS = ["Engineering", "Site Operations"];

const dayKind = (d, rest) =>
  REG_HOL.includes(d)
    ? "REG"
    : SPC_HOL.includes(d)
      ? "SPC"
      : wd(d) === rest
        ? "REST"
        : "WORK";

function presentOn(s, dep, date) {
  if (Array.isArray(s.workLogs))
    return s.workLogs.some(
      (l) =>
        l.deployment === dep.id && l.status === "APPROVED" && l.date === date,
    );
  return Object.entries(s.att).some(
    ([k, v]) => k.startsWith(dep.id + "|") && v.dates.includes(date),
  );
}

function lastWorkday(date, rest) {
  let x = addDays(date, -1);
  for (
    let i = 0;
    i < 7 && (wd(x) === rest || REG_HOL.includes(x) || SPC_HOL.includes(x));
    i++
  )
    x = addDays(x, -1);
  return x;
}

function depPay(s, d, from, to) {
  if (Array.isArray(s.workLogs)) return deploymentPayFromLogs(s, d, from, to);
  const a = s.att[d.id + "|" + from] || { dates: [], ot: 0, nd: 0, units: 0 },
    p = s.projects.find((x) => x.code === d.project),
    rg = p.region,
    rest = d.rest ?? 0,
    present = new Set(a.dates),
    end = d.endedOn || "9999-12-31";
  const r = {
    worked: 0,
    days: { WORK: 0, REST: 0, REG: 0, SPC: 0 },
    holU: 0,
    basic: 0,
    prem: 0,
    holPay: 0,
    ot: 0,
    nd: 0,
    topUp: 0,
    earn: 0,
    floor: 0,
    diff: 0,
    mwe: true,
    base13: 0,
    bill: 0,
    allow: 0,
  };
  let rateSum = 0;
  for (let x = from; x <= to; x = addDays(x, 1)) {
    if (x < d.from || x > end) continue;
    const k = dayKind(x, rest),
      mw = minWage(rg, x).daily;
    if (present.has(x)) {
      r.worked++;
      r.days[k]++;
      r.floor += mw;
      if (d.type === "DAILY") {
        const rate = Math.max(d.rate, mw);
        if (d.rate < mw) r.diff += mw - d.rate;
        if (d.rate > mw) r.mwe = false;
        r.basic += rate;
        r.prem += Math.round(rate * MULT[k]);
        rateSum += rate;
      }
    } else if (k === "REG" && presentOn(s, d, lastWorkday(x, rest))) {
      r.holU++;
      if (d.type === "DAILY") r.holPay += Math.max(d.rate, mw);
    }
  }
  if (d.type === "PIECE") {
    r.earn = Math.round(d.rate * (a.units || 0));
    r.topUp = Math.max(0, r.floor - r.earn);
    const avg = r.worked ? Math.round((r.earn + r.topUp) / r.worked) : 0;
    r.basic = r.earn + r.topUp;
    r.prem = Math.round(
      avg *
        (MULT.REST * r.days.REST +
          MULT.REG * r.days.REG +
          MULT.SPC * r.days.SPC),
    );
    r.holPay = avg * r.holU;
    rateSum = avg * r.worked;
    r.mwe = avg <= minWage(rg, to).daily;
  }
  const hourly = r.worked ? rateSum / r.worked / 8 : 0;
  r.ot = Math.round(hourly * 1.25 * (a.ot || 0));
  r.nd = Math.round(hourly * 0.1 * (a.nd || 0));
  r.gross = r.basic + r.prem + r.holPay + r.ot + r.nd;
  r.base13 = r.basic;
  r.allow = (d.perDiem || 0) * r.worked;
  r.bill =
    d.type === "DAILY"
      ? Math.round(d.bill * r.worked + (d.bill / 8) * 1.25 * (a.ot || 0))
      : Math.round(d.bill * (a.units || 0));
  r.units = a.units || 0;
  return r;
}

function fieldLine(s, e, from, to) {
  const parts = s.deployments
    .filter(
      (d) =>
        d.emp === e.id && d.from <= to && (!d.endedOn || d.endedOn >= from),
    )
    .map((d) => ({ dep: d.id, project: d.project, ...depPay(s, d, from, to) }))
    .filter((x) => x.gross > 0 || x.allow > 0);
  if (!parts.length) return null;
  const sum = (k) => parts.reduce((a, x) => a + x[k], 0),
    gross = sum("gross"),
    allow = sum("allow"),
    mwe = parts.every((x) => x.mwe),
    c = contrib(gross * 2),
    h = (x) => Math.round(x / 2);
  const L = {
    emp: e.id,
    field: true,
    gross,
    allow,
    mwe,
    base13: sum("base13"),
    worked: sum("worked"),
    parts,
    sssEE: h(c.sssEE),
    phEE: h(c.phEE),
    hdEE: h(c.hdEE),
    sssER: h(c.sssER),
    ec: h(c.ec),
    phER: h(c.phER),
    hdER: h(c.hdER),
  };
  const taxable = gross - L.sssEE - L.phEE - L.hdEE;
  L.tax = mwe ? 0 : semiMonthlyTax(Math.max(0, taxable));
  L.net = taxable - L.tax + allow;
  L.er = L.sssER + L.ec + L.phER + L.hdER;
  return L;
}

function runLines(s, from, to) {
  const lines = s.employees
    .filter((e) => e.pay !== "HRM_DRAFT")
    .map((e) =>
      e.pay === "FIELD"
        ? fieldLine(s, e, from, to)
        : ((L) => Object.assign(L, { allow: 0, base13: L.gross }))(payLine(e)),
    )
    .filter(Boolean);
  const prior = s.runs.filter(
    (r) =>
      r.status !== "DRAFT" &&
      r.from.slice(0, 7) === from.slice(0, 7) &&
      r.to < from,
  );
  for (const l of lines) {
    const employee = s.employees.find((e) => e.id === l.emp),
      previous = prior.flatMap((r) => r.lines).filter((x) => x.emp === l.emp),
      grossToDate = previous.reduce((a, x) => a + x.gross, 0) + l.gross,
      basicToDate =
        previous.reduce((a, x) => a + (x.base13 || x.gross), 0) +
        (l.base13 || l.gross),
      final = +to.slice(-2) > 15;
    const allowancesToDate = employee.allowanceExempt
        ? 0
        : previous.reduce((a, x) => a + (x.allow || 0), 0) + (l.allow || 0),
      sssBase = grossToDate + allowancesToDate;
    const contributions = contrib(
      final ? sssBase : sssBase * 2,
      final ? basicToDate : basicToDate * 2,
    );
    for (const key of ["sssEE", "sssER", "ec", "phEE", "phER", "hdEE", "hdER"])
      l[key] = final
        ? contributions[key] - previous.reduce((a, x) => a + (x[key] || 0), 0)
        : Math.round(contributions[key] / 2);
    const taxableAllowance = employee.allowanceExempt ? 0 : l.allow || 0,
      taxable = Math.max(
        0,
        l.gross + taxableAllowance - l.sssEE - l.phEE - l.hdEE,
      );
    l.tax =
      employee.mwe || l.mwe
        ? semiMonthlyTax(taxableAllowance)
        : semiMonthlyTax(taxable);
    l.er = l.sssER + l.ec + l.phER + l.hdER;
    l.net = l.gross - l.sssEE - l.phEE - l.hdEE - l.tax + (l.allow || 0);
    l.accrual13 = Math.round((l.base13 || 0) / 12);
  }
  return lines;
}

function mkRun(s, from, to, pay, by) {
  const r = {
    id: "PR-" + String(++s.seq.run).padStart(3, "0"),
    from,
    to,
    pay,
    status: "DRAFT",
    by,
    lines: runLines(s, from, to),
  };
  s.runs.push(r);
  return r;
}

function runTot(r) {
  const t = {
    gross: 0,
    sssEE: 0,
    phEE: 0,
    hdEE: 0,
    tax: 0,
    allow: 0,
    net: 0,
    er: 0,
    sssER: 0,
    ec: 0,
    phER: 0,
    hdER: 0,
  };
  r.lines.forEach((l) => {
    for (const k in t) t[k] += l[k] || 0;
  });
  return t;
}

function postAccrual(s, r) {
  const t = runTot(r),
    costs = [];
  for (const l of sitePayroll(r, s)) {
    const direct = DIRECT_DEPTS.includes(
        s.employees.find((e) => e.id === l.emp)?.dept,
      ),
      project = l.project || "";
    for (const [account, amount] of [
      [direct ? "5100" : "5010", l.gross],
      ["5050", l.sssER],
      ["5055", l.ec],
      ["5060", l.phER],
      ["5070", l.hdER],
      ["5120", l.allow],
      ["5101", l.accrual13],
    ])
      if (amount) costs.push({ a: account, d: amount, c: 0, project });
  }
  const accrual13 = r.lines.reduce((a, l) => a + (l.accrual13 || 0), 0);
  s.je.push({
    id: "JV-" + String(++s.seq.je).padStart(4, "0"),
    date: r.pay,
    memo: `Payroll accrual ${r.id} (${r.from} to ${r.to}); costs allocated to project and site`,
    src: r.id,
    status: "POSTED",
    by: r.by,
    ap: r.ap,
    lines: [
      ...costs,
      { a: "2210", d: 0, c: t.sssEE + t.sssER + t.ec },
      { a: "2220", d: 0, c: t.phEE + t.phER },
      { a: "2225", d: 0, c: t.hdEE + t.hdER },
      { a: "2235", d: 0, c: t.tax },
      { a: "2260", d: 0, c: t.net },
      ...(accrual13 ? [{ a: "2140", d: 0, c: accrual13 }] : []),
    ].map((l) => ({
      ...l,
      d: Math.max(0, l.d) + Math.max(0, -l.c),
      c: Math.max(0, l.c) + Math.max(0, -l.d),
    })),
  });
}

function postRelease(s, r) {
  const t = runTot(r);
  s.je.push({
    id: "JV-" + String(++s.seq.je).padStart(4, "0"),
    date: r.pay,
    memo: `Net pay released ${r.id}`,
    src: r.id,
    status: "POSTED",
    by: r.rel,
    ap: r.rel,
    lines: [
      { a: "2260", d: t.net, c: 0 },
      { a: "1020", d: 0, c: t.net },
    ],
  });
}

function sitePayroll(r, state) {
  const keys = [
      "gross",
      "sssEE",
      "phEE",
      "hdEE",
      "tax",
      "allow",
      "net",
      "er",
      "sssER",
      "ec",
      "phER",
      "hdER",
      "base13",
      "accrual13",
    ],
    out = [];
  for (const l of r.lines) {
    const parts =
        l.field && l.parts?.length
          ? l.parts
          : [
              {
                project: l.project || "",
                gross: l.gross,
                allow: l.allow || 0,
                base13: l.base13 || 0,
              },
            ],
      rows = parts.map((x) => ({
        emp: l.emp,
        project:
          x.project ??
          state?.deployments.find((d) => d.id === x.dep)?.project ??
          "",
      }));
    for (const k of keys) {
      const weights = parts.map((x) =>
          Math.max(
            0,
            k === "allow"
              ? x.allow || 0
              : k === "accrual13"
                ? x.base13 || 0
                : x.gross || 0,
          ),
        ),
        total = weights.reduce((a, b) => a + b, 0);
      let allocated = 0;
      rows.forEach((row, i) => {
        const amount =
          i === rows.length - 1
            ? (l[k] || 0) - allocated
            : Math.round(
                (l[k] || 0) * (total ? weights[i] / total : 1 / rows.length),
              );
        row[k] = amount;
        allocated += amount;
      });
    }
    rows.forEach(
      (row) =>
        (row.net =
          row.gross - row.sssEE - row.phEE - row.hdEE - row.tax + row.allow),
    );
    out.push(...rows);
  }
  return out;
}

export {
  RULES,
  contrib,
  BR,
  annualTax,
  payLine,
  MINW,
  REGION_NAME,
  minWage,
  REG_HOL,
  SPC_HOL,
  MULT,
  DIRECT_DEPTS,
  dayKind,
  presentOn,
  lastWorkday,
  depPay,
  fieldLine,
  runLines,
  mkRun,
  runTot,
  postAccrual,
  postRelease,
  sitePayroll,
};

// BIR RR 11-2018 Annex E, semi-monthly table effective 1 January 2023.
export function semiMonthlyTax(taxable) {
  const brackets = [
    [0, 0, 0],
    [1041700, 0, 0.15],
    [1666700, 93750, 0.2],
    [3333300, 427070, 0.25],
    [8333300, 1677070, 0.3],
    [33333300, 9177070, 0.35],
  ];
  let b = brackets[0];
  for (const candidate of brackets) if (taxable >= candidate[0]) b = candidate;
  return Math.max(0, Math.round(b[1] + (taxable - b[0]) * b[2]));
}
export function deploymentPayFromLogs(s, d, from, to) {
  const project = s.projects.find((p) => p.code === d.project),
    a = approvedAttendance(s, d, from, to),
    r = {
      worked: 0,
      days: { WORK: 0, REST: 0, REG: 0, SPC: 0 },
      holU: 0,
      basic: 0,
      prem: 0,
      holPay: 0,
      ot: 0,
      nd: 0,
      topUp: 0,
      earn: 0,
      floor: 0,
      diff: 0,
      mwe: true,
      base13: 0,
      bill: 0,
      allow: 0,
      units: 0,
    };
  for (const l of a.logs) {
    const fraction = l.hours / 8,
      mw = minWage(project.region, l.date).daily;
    if (!mw)
      throw new Error(
        "No verified minimum wage rule for " + project.region + " on " + l.date,
      );
    const holiday = REG_HOL.includes(l.date),
      special = SPC_HOL.includes(l.date),
      rest = wd(l.date) === (d.rest ?? 0),
      mult = holiday
        ? rest
          ? 2.6
          : 2
        : special
          ? rest
            ? 1.5
            : 1.3
          : rest
            ? 1.3
            : 1,
      otMult = mult === 1 ? 1.25 : 1.3;
    const floor = Math.round(mw * fraction),
      earned =
        l.type === "PIECE"
          ? Math.round(l.payRate * l.units)
          : Math.round(l.payRate * fraction),
      basic = Math.max(earned, floor),
      hourly = l.hours ? basic / l.hours : 0;
    r.worked += fraction;
    r.days[dayKind(l.date, d.rest ?? 0)] += fraction;
    r.floor += floor;
    r.earn += earned;
    r.basic += basic;
    r.topUp += l.type === "PIECE" ? Math.max(0, floor - earned) : 0;
    r.diff += l.type === "DAILY" ? Math.max(0, floor - earned) : 0;
    r.prem += Math.round(basic * (mult - 1));
    r.ot += Math.round(hourly * mult * otMult * l.ot);
    r.nd += Math.round(
      hourly * mult * 0.1 * (l.night - (l.nightOT || 0)) +
        hourly * mult * otMult * 0.1 * (l.nightOT || 0),
    );
    r.allow += Math.round(l.allowance * fraction);
    r.units += l.units;
    r.bill +=
      l.type === "PIECE"
        ? Math.round(l.billRate * l.units)
        : Math.round(
            l.billRate * fraction + (l.billRate / 8) * mult * otMult * l.ot,
          );
    if (hourly > mw / 8 + 0.01) r.mwe = false;
  }
  for (let date = from; date <= to; date = addDays(date, 1)) {
    if (
      date < d.from ||
      date > (d.endedOn || d.to) ||
      !REG_HOL.includes(date) ||
      a.dates.includes(date)
    )
      continue;
    const prev = lastWorkday(date, d.rest ?? 0),
      prior = (s.workLogs || []).find(
        (l) =>
          l.deployment === d.id && l.status === "APPROVED" && l.date === prev,
      );
    if (prior) {
      r.holU += prior.hours / 8;
      r.holPay += Math.round(
        (Math.max(prior.payRate, minWage(project.region, date).daily) *
          prior.hours) /
          8,
      );
    }
  }
  r.gross = r.basic + r.prem + r.holPay + r.ot + r.nd;
  r.base13 = r.basic;
  return r;
}
